import { IconCurrencyDollar } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import './TeamForm.scss';
import { Row, Col } from 'reactstrap';
import {
    NSFormGroup, NSInput, NSLabel, NSSelect, NSCard,
    NSButton,
    NSContactChip,
} from 'bricks';
import { TeamData, VendorData } from 'views/InvestmentMemo/types';
import { TEAM_VIEW_ENTITY, TEAM_VENDORS } from 'views/InvestmentMemo/constants';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { IContact } from 'views/DealDashboard/types';
import { ICompany } from 'components/DealForm/types';
import { useGetOrganizationCompanies } from 'components/DealForm/hooks/useGetOrganizationCompanies';
import getCRMDealContactsQueryOptions from '../../../../DealDashboard/Activity/hooks/useGetCRMDealContacts';
import { useInvestmentMemoContext } from '../../../InvestmentMemoContext';

export interface TeamFormProps {
    data: TeamData;
    onChange: (data: Partial<TeamData>) => void;
    selectedDealId: number | null;
}

const TeamForm = ({ data, onChange, selectedDealId }: TeamFormProps) => {
    const { shouldShowError, getFieldError } = useInvestmentMemoContext();
    const [view, setView] = useState<typeof TEAM_VIEW_ENTITY[keyof typeof TEAM_VIEW_ENTITY]>(TEAM_VIEW_ENTITY.CONTACTS);
    const [principalInput, setPrincipalInput] = useState('');
    const [selectedContact, setSelectedContact] = useState<Array<{ label: string; value: number }>>([]);
    const { selectedOrganizationId } = useAuthContext();

    const {
        data: contacts,
    } = useQuery(getCRMDealContactsQueryOptions(selectedOrganizationId!, selectedDealId!));

    const {
        data: companies,
    } = useGetOrganizationCompanies();

    const handleAddPrincipalManually = () => {
        if (principalInput.trim()) {
            onChange({ principals: [...data.principals, principalInput.trim()] });
            setPrincipalInput('');
        }
    };

    const handleAddPrincipalFromContact = () => {
        if (selectedContact && selectedContact.length > 0) {
            const newPrincipals = selectedContact
                .map(option => option.label)
                .filter(label => !data.principals.includes(label));
            if (newPrincipals.length > 0) {
                onChange({ principals: [...data.principals, ...newPrincipals] });
            }
            setSelectedContact([]);
        }
    };

    const selectContactOptions = (contacts || []).map((contact: IContact) => ({
        label: contact.name,
        value: contact.id,
    }));

    const selectVendorOptions = (companies || []).map((company: ICompany) => ({
        label: company.name,
        value: company.correlationId,
    }));

    const handleVendorSelectorChange = (key: string, value: string) => {
        onChange({ vendors: { ...data.vendors, [key]: value } });
    };

    return (
        <div className="px-3 pb-3">
            <NSFormGroup>
                <NSInput
                    name="sponsorCompanyName"
                    type="text"
                    label="Sponsor company name"
                    value={data.sponsorCompanyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ sponsorCompanyName: e.target.value })}
                    required
                    showErrorMsg={shouldShowError('team.sponsorCompanyName')}
                    errorMsg={getFieldError('team.sponsorCompanyName')}
                />
            </NSFormGroup>
            <NSFormGroup>
                <NSInput
                    name="sponsorBio"
                    type="text"
                    label="Sponsor bio (optional)"
                    value={data.sponsorBio}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ sponsorBio: e.target.value })}
                />
            </NSFormGroup>
            <NSFormGroup>
                <NSInput
                    id="assetsUnderManagement"
                    type="number"
                    label="Assets under management (optional)"
                    name="assetsUnderManagement"
                    inputClassName="mb-1"
                    prependInputAddon={<NSIcon icon={IconCurrencyDollar} />}
                    value={data.assetsUnderManagement}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ assetsUnderManagement: e.target.value })}
                />
            </NSFormGroup>
            <NSFormGroup>
                <NSLabel for="principals" required>Principals</NSLabel>
            </NSFormGroup>
            {(data.principals || []).map((name: string, idx: number) => (
                <NSContactChip
                    key={`${name}-${idx}`} // eslint-disable-line react/no-array-index-key
                    name={name}
                    onRemove={() => {
                        const newPrincipals = [...data.principals];
                        newPrincipals.splice(idx, 1);
                        onChange({ principals: newPrincipals });
                    }}
                />
            ))}
            <NSFormGroup className="mb-0 mt-1">
                <NSButton
                    text="Enter manually"
                    color="secondary"
                    className={classNames('mr-2 mb-2 font-12', { 'border border-primary bg-primary-lighten': view === TEAM_VIEW_ENTITY.MANUALLY })}
                    callback={() => setView(TEAM_VIEW_ENTITY.MANUALLY)}
                    size="sm"
                />
                <NSButton
                    text="Select from contact list"
                    color="secondary"
                    className={classNames('mb-2 font-12', { 'border border-primary bg-primary-lighten': view === TEAM_VIEW_ENTITY.CONTACTS })}
                    callback={() => setView(TEAM_VIEW_ENTITY.CONTACTS)}
                    size="sm"
                />
            </NSFormGroup>
            <RenderIf isTrue={view === TEAM_VIEW_ENTITY.MANUALLY}>
                <Row>
                    <Col md={12} className="d-flex">
                        <NSInput
                            name="principals"
                            type="text"
                            value={principalInput}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrincipalInput(e.target.value)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter' && principalInput.trim()) {
                                    handleAddPrincipalManually();
                                }
                            }}
                        />
                        <NSButton
                            className="border border-primary text-primary rounded ml-2"
                            callback={handleAddPrincipalManually}
                            disabled={!principalInput.trim()}
                        >
                            Add
                        </NSButton>
                    </Col>
                </Row>
            </RenderIf>
            <RenderIf isTrue={view === TEAM_VIEW_ENTITY.CONTACTS}>
                <Row className="align-items-center">
                    <Col>
                        <NSSelect
                            name="contactList"
                            options={selectContactOptions}
                            value={selectedContact}
                            onChange={setSelectedContact}
                            isClearable
                            isMulti
                        />
                    </Col>
                    <NSButton
                        className="border border-primary text-primary rounded mr-2"
                        callback={handleAddPrincipalFromContact}
                        disabled={!selectedContact || selectedContact.length === 0}
                    >
                        Add
                    </NSButton>
                </Row>
                {shouldShowError('team.principals') && data.principals.length === 0 && (
                    <p className="text-danger small mt-1 mb-0">
                        At least one principal is required
                    </p>
                )}
                <NSCard className="px-3 pt-3 pb-2 mt-2 NSCard--level-3">
                    <div className="mb-0">
                        <p className="text-dark">Select vendors to include</p>
                        {TEAM_VENDORS.map(({ key, label }) => {
                            const vendorId = data.vendors?.[key as keyof VendorData];
                            const matchingCompany = vendorId
                                ? companies?.find(c => c.correlationId === vendorId)
                                : undefined;

                            const vendorValue = matchingCompany
                                ? { label: matchingCompany.name, value: matchingCompany.correlationId }
                                : null;

                            return (
                                <div key={key} className="mb-2">
                                    <NSLabel>{label}</NSLabel>
                                    <NSSelect
                                        name={key}
                                        options={selectVendorOptions}
                                        value={vendorValue}
                                        onChange={(option: { label: string; value: string } | null) => (
                                            handleVendorSelectorChange(key, option ? option.value : '')
                                        )}
                                        isClearable={false}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </NSCard>
            </RenderIf>
        </div>
    );
};

export default TeamForm;
